import React from 'react';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { DATA_LAYERS } from '../../../lib/dataLayers';
import { useGrowthCurves } from '../../../hooks/useGrowthCurves';
import { GrowthCurvePoint } from '../../../lib/types';
import { shortTons } from '../../../lib/units';
import {
  CHART_TOOLTIP_STYLE,
  CHART_AXIS_STYLE,
  CHART_GRID_STROKE,
  CHART_AXIS_STROKE,
  CHART_PRIMARY_COLOR,
} from '../../../lib/chartStyles';
import { LayerHeader } from './LayerHeader';
import { AboutLayer } from './AboutLayer';
import {
  LayerLoadingLabel,
  LayerErrorAlert,
  LayerLiveBadge,
  SkeletonPulseBlock,
} from './contentPrimitives';

interface GrowthCurvesContentProps {
  millCode: string | null;
  driveTimeMinutes: number;
}

const Skeleton: React.FC = () => (
  <div className="space-y-4">
    <LayerLoadingLabel layer={DATA_LAYERS.growth_curves} />
    <SkeletonPulseBlock className="h-[280px]" />
  </div>
);

export const GrowthCurvesContent: React.FC<GrowthCurvesContentProps> = ({
  millCode,
  driveTimeMinutes,
}) => {
  const layer = DATA_LAYERS.growth_curves;
  const { data, isLoading, error } = useGrowthCurves(millCode, driveTimeMinutes);

  // Recharts' Area accepts a 2-tuple per point for banded fills. Convert
  // Mg/ha/yr from S3 to short tons/ha/yr at the display boundary —
  // mass-only conversion (Mg → tons), area stays in hectares.
  const chartData = (data?.points ?? []).map((p: GrowthCurvePoint) => ({
    year: p.year,
    mean: shortTons(p.mean),
    band: [shortTons(p.p5), shortTons(p.p95)] as [number, number],
  }));

  return (
    <div className="space-y-6">
      <LayerHeader layer={layer} />

      <AboutLayer>
        <p>
          These curves project the mean annual forest growth rate within the
          supply shed from 2033 to 2132 in tons of biomass per hectare per year
          &mdash; how much new biomass the forest is expected to add each year
          if no major disturbance occurs. Projections are modeled outputs and
          inherently uncertain, especially decades into the future.
        </p>
        <p>
          The shaded band is the 5th&ndash;95th percentile range across model
          runs. Wider bands indicate greater uncertainty &mdash; typical for
          longer time horizons, novel climate conditions, or supply sheds with
          mixed forest types.
        </p>
      </AboutLayer>

      {isLoading && <Skeleton />}

      {!isLoading && error && (
        <LayerErrorAlert layer={layer} details={error} />
      )}

      {!isLoading && data && data.points.length > 0 && (
        <>
          <LayerLiveBadge />

          <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
            <h4 className="text-base font-roboto font-bold text-figma-text mb-4">
              Projected Growth Rate ({data.points[0].year}-{data.points[data.points.length - 1].year}) <span className="text-neutral-500 font-normal">(tons/ha/yr)</span>
            </h4>
            <ResponsiveContainer width="100%" height={280} minWidth={0}>
              <ComposedChart data={chartData} margin={{ top: 5, right: 12, left: 8, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                <XAxis
                  dataKey="year"
                  stroke={CHART_AXIS_STROKE}
                  style={CHART_AXIS_STYLE}
                  tickMargin={6}
                />
                <YAxis
                  stroke={CHART_AXIS_STROKE}
                  style={CHART_AXIS_STYLE}
                  tickFormatter={(v: number) => v.toFixed(1)}
                  width={44}
                />
                <Tooltip
                  contentStyle={CHART_TOOLTIP_STYLE}
                  formatter={(value: number | number[] | undefined, name: string | undefined) => {
                    const label = name ?? '';
                    if (Array.isArray(value)) {
                      return [`${value[0].toFixed(2)} - ${value[1].toFixed(2)} tons/ha/yr`, label];
                    }
                    return [`${(value ?? 0).toFixed(2)} tons/ha/yr`, label];
                  }}
                  labelFormatter={(label) => `Year ${label}`}
                />
                <Legend
                  wrapperStyle={{ fontSize: '12px', fontFamily: 'Roboto, sans-serif' }}
                />
                <Area
                  type="monotone"
                  dataKey="band"
                  name="5th-95th percentile"
                  stroke="none"
                  fill={CHART_PRIMARY_COLOR}
                  fillOpacity={0.18}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="mean"
                  name="Mean"
                  stroke={CHART_PRIMARY_COLOR}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
            <p className="text-xs font-roboto italic text-neutral-500 mt-2 leading-relaxed">
              Dark line = most likely projected mean growth rate. Shaded band = 90%
              confidence range (5th&ndash;95th percentile). A wider band means
              less certainty in the projection.
            </p>
          </div>
        </>
      )}
    </div>
  );
};
